/**
 * Relay for the native `self_healing:terminal_outcome` event.
 * The event lets the FE learn how a stop ended when its own `stopSession()`
 * timed out, or when the stop came from elsewhere (watchdog, another window).
 * Outcomes are buffered because the event can arrive before the awaiter
 * registers, keyed by `(recordingId, generation)`.
 */
import type { BackupMode, StopReason, TerminalClass } from '@/types/self-healing-stop';

/** The `self_healing:terminal_outcome` payload (camelCase across the IPC). */
export interface NativeTerminalOutcome {
  recordingId: string;
  generation: number;
  reason: StopReason;
  classification: TerminalClass;
  finishSucceeded: boolean;
  failedChunks: number;
  totalChunks: number;
  backupUploaded: boolean;
  filesRetained: boolean;
  recorderStopped: boolean;
  cadenceStopped: boolean;
  workerDrained: boolean;
  durationMs: number;
  backup: BackupMode;
  /** The pipeline's raw backup disposition, which `backup` can differ from when no dispatch ran. */
  backupPendingAsync: boolean;
  backupRetained: boolean;
  /** The stop moved its source WAVs off the live paths, so they must not be re-read. */
  backupDeferred: boolean;
  /** Local offline consult this stop handed unsent audio to, if any. */
  offlineHandoverLocalSessionId?: string;
}

const key = (recordingId: string, generation: number) => `${recordingId}:${generation}`;

/**
 * Bounded so a long-lived desktop process cannot accumulate an entry per
 * recording. Two readers use it: an in-flight stop (eviction falls back to its
 * own timeout) and the close-guard release gate (eviction makes its per-epoch
 * signal decline, which fails towards not releasing). Evicting an entry needs
 * 16 other recordings to finalize inside a reader's window, so 16 is enough.
 * Not aligned with native's `MAX_CACHED_TERMINAL_OUTCOMES`: that is an
 * idempotency cache with a different job.
 */
const MAX_BUFFERED_OUTCOMES = 16;

const outcomes = new Map<string, NativeTerminalOutcome>();

/** Called by the `self_healing:terminal_outcome` listener. */
export function recordTerminalOutcome(outcome: NativeTerminalOutcome): void {
  if (!outcome?.recordingId) return;
  const id = key(outcome.recordingId, outcome.generation);

  outcomes.set(id, outcome);
  while (outcomes.size > MAX_BUFFERED_OUTCOMES) {
    const oldest = outcomes.keys().next().value;
    if (oldest === undefined) break;
    outcomes.delete(oldest);
  }
}

/**
 * The typed outcome for this epoch, if one has already arrived.
 * A lookup rather than a wait: after the FE exhausts its `stopSession()`
 * attempts, the outcome is either buffered here or not coming.
 */
export function peekTerminalOutcome(
  recordingId: string,
  generation: number
): NativeTerminalOutcome | undefined {
  return outcomes.get(key(recordingId, generation));
}

/**
 * The latest buffered outcome for a recording id, whatever its generation.
 * Recovery rows carry no generation, so they cannot form the buffer key.
 * "Latest" means highest generation, not iteration order (`Map.set` on an
 * existing key keeps its position). Returns `undefined` when nothing is
 * buffered, which callers must read as "cannot tell", never "ended cleanly".
 */
export function peekTerminalOutcomeForRecording(
  recordingId: string
): NativeTerminalOutcome | undefined {
  let latest: NativeTerminalOutcome | undefined;
  for (const outcome of outcomes.values()) {
    if (outcome.recordingId !== recordingId) continue;
    if (!latest || outcome.generation > latest.generation) latest = outcome;
  }
  return latest;
}

/**
 * Whether a typed outcome may advance to note generation.
 * Only a final user stop generates. Pause, device switch, `feFallback` and
 * every watchdog or shutdown reason do not, matching Rust's
 * `StopReason::triggers_note_generation`.
 */
export function outcomeMayGenerateNote(reason: StopReason): boolean {
  return reason === 'userStop';
}

/**
 * Project a native terminal outcome onto the shape the existing stop-health
 * gate reads, so recovering from a timeout goes through exactly the same
 * decision as a normally-resolved stop rather than a parallel one.
 */
export function terminalOutcomeAsStopResult(outcome: NativeTerminalOutcome): {
  totalChunks: number;
  totalBytes: number;
  failedChunks: number;
  durationMs: number;
  backupUploaded: boolean;
  finishSucceeded: boolean;
  backup: BackupMode;
  backupPendingAsync: boolean;
  backupRetained: boolean;
  backupDeferred: boolean;
  offlineHandoverLocalSessionId?: string;
} {
  return {
    totalChunks: outcome.totalChunks,
    totalBytes: 0,
    failedChunks: outcome.failedChunks,
    durationMs: outcome.durationMs,
    backupUploaded: outcome.backupUploaded,
    finishSucceeded: outcome.finishSucceeded,
    backup: outcome.backup,
    backupPendingAsync: outcome.backupPendingAsync,
    backupRetained: outcome.backupRetained,
    backupDeferred: outcome.backupDeferred,
    offlineHandoverLocalSessionId: outcome.offlineHandoverLocalSessionId,
  };
}

/** Test seam: the buffer is module state that would otherwise leak between specs. */
export function __resetTerminalOutcomeRelay(): void {
  outcomes.clear();
}
